import { Contract, JsonRpcProvider, BrowserProvider, Provider, Signer } from "ethers";

/**
 * Cat Claw contract configuration and ethers v6 wrappers.
 *
 * Exports:
 * 1. Constants: addresses, ABI paths, and asset list (no web3 dependency).
 * 2. Ethers helpers: `getProvider`, `getContract`, `getContracts` for direct chain access.
 */

interface WindowWithEthereum extends Window {
  ethereum?: import("ethers").Eip1193Provider;
}

export const ABI_PATHS = {
  CatStonks: "/abis/CatStonks.json",
  CatStonksRounds: "/abis/CatStonksRounds.json",
  CatStonksVault: "/abis/CatStonksVault.json",
  ERC20: "/abis/MockERC20.json",
  WETH: "/abis/MockWETH.json",
} as const;

export const ADDRESSES = {
  chainId: Number(import.meta.env.VITE_CHAIN_ID ?? 4663),
  rpcUrl: import.meta.env.VITE_RPC_URL as string,

  // Tokens
  CATCLAW: import.meta.env.VITE_CATCLAW_TOKEN_ADDRESS as string,
  NVDA: import.meta.env.VITE_NVDA_TOKEN_ADDRESS as string,
  SPCX: import.meta.env.VITE_SPCX_TOKEN_ADDRESS as string,
  SPY: import.meta.env.VITE_SPY_TOKEN_ADDRESS as string,
  WETH: import.meta.env.VITE_WETH_TOKEN_ADDRESS as string,

  // Core contracts
  CatStonks: import.meta.env.VITE_CATSTONKS_NFT_ADDRESS as string,
  CatStonksRounds: import.meta.env.VITE_CATSTONKS_ROUNDS_ADDRESS as string,
  CatStonksVault: import.meta.env.VITE_CATSTONKS_VAULT_ADDRESS as string,

  // Misc
  Router: import.meta.env.VITE_UNISWAP_ROUTER_ADDRESS as string,
  Treasury: import.meta.env.VITE_TREASURY_ADDRESS as string,
} as const;

/**
 * NFT 合约部署所在区块。
 *
 * 扫描 Transfer 日志时以此为起点，避免从创世块扫 4800 万个区块。
 * 未配置时退回 0（可用但很慢）。
 */
export const DEPLOY_BLOCK = Number(import.meta.env.VITE_DEPLOY_BLOCK ?? 0);

export const MINT_PRICE_WEI = BigInt(
  import.meta.env.VITE_MINT_PRICE_WEI ?? "10000000000000000"
);

/** Minimal ERC-20 ABI (name, symbol, decimals, balanceOf, approve, transfer, allowance, events). */
export const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)",
];

/** Minimal CatStonks NFT ABI covering common frontend calls and events. */
export const CATSTONKS_ABI = [
  // Reads
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function totalSupply() view returns (uint256)",
  "function totalMinted() view returns (uint256)",
  "function MAX_SUPPLY() view returns (uint256)",
  "function MAX_PER_TX() view returns (uint256)",
  "function mintPriceWei() view returns (uint256)",
  "function mintOpen() view returns (bool)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function tokenURIOf(uint256 tokenId) view returns (string)",
  "function rarityWeight(uint256 tokenId) view returns (uint256)",
  "function tierBurned(uint256 tokenId) view returns (uint256)",
  "function isActive(uint256 tokenId) view returns (bool)",
  "function weightOf(uint256 tokenId) view returns (uint256)",
  "function rounds() view returns (address)",
  "function TIER_BURN(uint8 tier) view returns (uint256)",
  "function TIER_WEIGHT(uint8 tier) view returns (uint256)",
  "function ACTIVATE_BURN() view returns (uint256)",
  "function FUSE_BURN_TWO() view returns (uint256)",
  "function FUSE_BURN_THREE() view returns (uint256)",

  // Writes
  "function mint(uint256 quantity, uint256[] calldata weights, string[] calldata uris, bytes32[][] calldata proofs) payable",
  "function reserveMint(uint256 weight, string calldata uri, bytes32[] calldata proof)",
  "function activate(uint256 tokenId)",
  "function upgradeTier(uint256 tokenId, uint8 tier)",
  "function fuse(uint256[] calldata tokenIds)",
  "function setApprovalForAll(address operator, bool approved)",
  "function transferFrom(address from, address to, uint256 tokenId)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)",

  // Events
  "event Minted(address indexed to, uint256 indexed tokenId)",
  "event Activated(uint256 indexed tokenId, address indexed owner, uint256 burned)",
  "event TierUpgraded(uint256 indexed tokenId, uint8 tier, uint256 totalBurned)",
  "event Fused(uint256 indexed survivorId, uint256[] absorbedIds, uint8 parts, uint256 stackBurned)",
  "event Deactivated(uint256 indexed tokenId)",
  "event RaritySet(uint256 indexed tokenId, uint256 weight)",
  "event TokenURISet(uint256 indexed tokenId, string uri)",
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
];

/** Minimal CatStonksRounds ABI. */
export const CATSTONKS_ROUNDS_ABI = [
  // Reads
  "function nft() view returns (address)",
  "function vault() view returns (address)",
  "function operator() view returns (address)",
  "function currentRound() view returns (uint256)",
  "function roundDuration() view returns (uint256)",
  "function totalWeight() view returns (uint256)",
  "function potBuffer() view returns (uint256)",
  "function accPerWeight(uint8 assetIdx) view returns (uint256)",
  "function assetCount() view returns (uint8)",
  "function assets(uint8 index) view returns (address token, address pool, address pool2, address midToken, bool isStock, bool active)",
  "function tokenState(uint256 tokenId) view returns (uint256 weight, uint256 liveFrom, uint8 splitCount, uint64 pendingCreditPacks, bool pendingDecrease)",
  "function splitOf(uint256 tokenId, uint8 assetIdx) view returns (uint16 bps)",

  // Writes
  "function settle()",
  "function sync(uint256[] calldata tokenIds)",
  "function deliver(uint256[] calldata tokenIds)",
  "function setSplit(uint256 tokenId, uint8[] calldata assetIdxs, uint16[] calldata bps)",
  "function addAsset(address token, address pool, address pool2, address midToken, bool isStock)",

  // Events
  "event RoundSettled(uint256 indexed round, uint256 pot, uint256 totalWeight, uint256 accPerWeight)",
  "event WeightSynced(uint256 indexed tokenId, uint256 weight, uint256 liveFrom)",
  "event Delivered(uint256 indexed tokenId, uint256[] amounts)",
  "event SplitSet(uint256 indexed tokenId, uint8[] assetIdxs, uint16[] bps)",
  "event AssetAdded(uint8 indexed assetIdx, address token)",
];

/** Minimal CatStonksVault ABI. */
export const CATSTONKS_VAULT_ABI = [
  // Reads
  "function nft() view returns (address)",
  "function engine() view returns (address)",
  "function assetToken(uint8 assetIdx) view returns (address)",
  "function assetCount() view returns (uint8)",
  "function balance(uint256 tokenId, uint8 assetIdx) view returns (uint256)",
  "function balancesOf(uint256 tokenId) view returns (address[] tokens, uint256[] amounts)",
  "function claimNonce(uint256 tokenId) view returns (uint256)",
  "function claimDigest(uint256 tokenId, uint256 nonce) view returns (bytes32)",

  // Writes
  "function claim(uint256 tokenId)",
  "function claimOne(uint256 tokenId, uint8 assetIdx)",
  "function claimFor(uint256 tokenId, uint256 nonce, bytes calldata signature)",

  // Events
  "event Claimed(uint256 indexed tokenId, address indexed token, uint256 amount)",
  "event Credited(uint256 indexed tokenId, uint8 indexed assetIdx, address token, uint256 amount)",
  "event Merged(uint256 indexed survivor, uint256 indexed absorbed, uint256 assetCount)",
];

/**
 * 只读 provider（单例）。
 *
 * 必须是单例：ethers v6 的 JsonRpcProvider 会把同一实例上短时间内的调用
 * 合并成一个 JSON-RPC batch 请求。每次 new 一个新实例会让批处理完全失效，
 * 几十个 eth_call 变成几十个独立 HTTP POST，直接被 RPC 网关 429 限流。
 */
let providerSingleton: JsonRpcProvider | null = null;

export function getProvider(): JsonRpcProvider {
  if (!providerSingleton) {
    providerSingleton = new JsonRpcProvider(ADDRESSES.rpcUrl, ADDRESSES.chainId, {
      // 链 ID 已知且不会变，跳过每次连接时的 eth_chainId 网络探测
      staticNetwork: true,
      // 40ms 聚合窗口：让不同 hook 在相近时刻发起的调用也能并进同一批
      batchStallTime: 40,
      batchMaxCount: 20,
      // 相同的只读请求在 2s 内复用结果
      cacheTimeout: 2000,
    });
  }
  return providerSingleton;
}

/** Get a signer from the browser wallet. */
export async function getBrowserSigner(): Promise<Signer> {
  const ethereum = (typeof window !== "undefined" ? (window as WindowWithEthereum).ethereum : undefined);
  if (!ethereum) {
    throw new Error("No browser wallet detected (MetaMask / Rabby / etc.)");
  }
  const provider = new BrowserProvider(ethereum, ADDRESSES.chainId);
  return provider.getSigner();
}

/** 只读 contract 实例缓存（key 为合约名） */
const readContractCache = new Map<string, Contract>();

/** Return a single typed contract instance. */
export function getContract(
  name: "CatStonks" | "CatStonksRounds" | "CatStonksVault" | "CATCLAW" | "NVDA" | "SPCX" | "SPY" | "WETH",
  signerOrProvider: Provider | Signer = getProvider()
): Contract {
  const address = ADDRESSES[name];
  if (!address) throw new Error(`Missing address configuration: ${name}`);

  const abiMap: Record<string, string[]> = {
    CatStonks: CATSTONKS_ABI,
    CatStonksRounds: CATSTONKS_ROUNDS_ABI,
    CatStonksVault: CATSTONKS_VAULT_ABI,
    CATCLAW: ERC20_ABI,
    NVDA: ERC20_ABI,
    SPCX: ERC20_ABI,
    SPY: ERC20_ABI,
    WETH: ERC20_ABI,
  };

  // 只读 contract 实例可复用，避免每轮轮询都重建
  if (signerOrProvider === providerSingleton) {
    const cached = readContractCache.get(name);
    if (cached) return cached;
    const created = new Contract(address, abiMap[name], signerOrProvider);
    readContractCache.set(name, created);
    return created;
  }

  return new Contract(address, abiMap[name], signerOrProvider);
}

/** Get all core contracts plus common ERC-20s. */
export function getContracts(signerOrProvider: Provider | Signer = getProvider()) {
  return {
    nft: getContract("CatStonks", signerOrProvider),
    rounds: getContract("CatStonksRounds", signerOrProvider),
    vault: getContract("CatStonksVault", signerOrProvider),
    catclaw: getContract("CATCLAW", signerOrProvider),
    nvda: getContract("NVDA", signerOrProvider),
    spcx: getContract("SPCX", signerOrProvider),
    spy: getContract("SPY", signerOrProvider),
    weth: getContract("WETH", signerOrProvider),
  } as const;
}

/** Derive the current tier from the cumulative CATCLAW burned amount. */
export function getTierFromBurned(tierBurned: bigint): number {
  if (tierBurned === 0n) return 0;
  // 合约中 TIER_BURN = [25_000e18, 75_000e18, 150_000e18, 300_000e18, 850_000e18]
  const thresholds = [
    75_000n * 10n ** 18n,
    150_000n * 10n ** 18n,
    300_000n * 10n ** 18n,
    850_000n * 10n ** 18n,
  ];
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (tierBurned >= thresholds[i]) return i + 1;
  }
  return 0;
}

/** Reward asset list. */
export const ASSETS = [
  { symbol: "NVDA", address: ADDRESSES.NVDA },
  { symbol: "SPCX", address: ADDRESSES.SPCX },
  { symbol: "SPY", address: ADDRESSES.SPY },
] as const;

/** Fetch full ABI JSON from the public folder. */
export async function fetchAbiJson(name: keyof typeof ABI_PATHS): Promise<unknown> {
  const res = await fetch(ABI_PATHS[name]);
  if (!res.ok) throw new Error(`Failed to load ABI: ${ABI_PATHS[name]}`);
  return res.json();
}
